"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { LobbyAvailableFilters } from "api"
import Link from "next/link"
import { CaseIcon, LocationIcon, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Job, jobClick } from "utils"
import { buildReferralQueryString } from "utils/referral"
import { CareerLobbyMessages } from "../Careers"

const StyledLinkWrapp = styled(Link)`
  display: block;
  text-decoration: none;
  cursor: pointer;
`

const StyledCard = styled(Box)`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-color: #ffffff;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    box-shadow: 0px 0px 16px 0px rgba(0, 38, 83, 0.1);
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 32px;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: flex-start;
    width: 295px;
  }
`

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 40px;
    width: auto;
  }
`

const InfoItem = styled.div<{ minWidthDesktop?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.colors.newBlack};
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: ${(props) => (props.minWidthDesktop ? props.minWidthDesktop + "px" : "auto")};
  }

  svg {
    flex-shrink: 0;
  }
`

const Actions = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const CtaButton = styled(Button)`
  padding: 8px 24px;
  min-width: 0;
  height: auto;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.darkBlue4rentMarketing};
  color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  text-transform: none;
  box-shadow: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
    background-color: #f6f8fb;
    box-shadow: none;
  }
`

const LocationSymbol = styled(LocationIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${(props) => props.$color};
  }
`

const CaseSymbol = styled(CaseIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    stroke: ${(props) => props.$color};
    stroke-opacity: 1;
  }
`

function JobCard({
  job,
  locale,
  messages,
  filters,
}: {
  job: Job
  locale: string
  messages: CareerLobbyMessages
  filters: LobbyAvailableFilters
}) {
  const theme = useTheme()
  const primaryBlue = theme.colors?.blue4rentMarketing
  const textBlack = theme.colors.newBlack

  const jobHref = `/${locale === "en" ? "en/" : ""}career/${
    job?.orderId
  }${buildReferralQueryString()}`
  const handleJobClick = () => jobClick(messages.viewJob, job?.jobName ? job?.jobName : "")

  const fields = [job?.professionName, job?.subfield]
    ?.filter((field) => Boolean(field))
    ?.join(" | ")

  const companyName =
    filters.avialableCompanies.find((filter) => filter.id === job?.branchId)?.name ||
    job?.department ||
    ""

  const cardContent = (
    <StyledCard
    // sx={{
    //   fontFamily: "var(--narkis-condensed) !important",
    // }}
    >
      <Content>
        <TitleBlock>
          {companyName && (
            <NewText
              component={"span"}
              desktopFontSize={18}
              mobileFontSize={14}
              desktopLineHeight={"1.5"}
              mobileLineHeight={"1.5"}
              color={textBlack}
              sx={{ letterSpacing: "-0.36px" }}
            >
              {companyName}
            </NewText>
          )}

          <NewText
            component={"h3"}
            desktopFontSize={24}
            mobileFontSize={20}
            desktopLineHeight={"1.5"}
            mobileLineHeight={"1.3"}
            desktopFontWeight={700}
            mobileFontWeight={700}
            color={theme.colors.darkBlue4rentMarketing}
            sx={{ letterSpacing: "-0.48px" }}
          >
            {job?.jobName}
          </NewText>
        </TitleBlock>
        <InfoBlock>
          <InfoItem minWidthDesktop={200}>
            <LocationSymbol $color={primaryBlue} role="img" aria-label="מיקום המשרה" />
            <NewText
              component={"span"}
              desktopFontSize={18}
              desktopFontWeight={300}
              mobileFontSize={16}
              mobileFontWeight={300}
              desktopLineHeight={"1.5"}
              mobileLineHeight={"1.45"}
              color={textBlack}
              sx={{ letterSpacing: "-0.36px" }}
            >
              {job?.areaName}
            </NewText>
          </InfoItem>
          {fields && (
            <InfoItem>
              <CaseSymbol $color={primaryBlue} role="img" aria-label="תחום המשרה" />
              <NewText
                component={"span"}
                desktopFontSize={18}
                desktopFontWeight={300}
                mobileFontSize={16}
                mobileFontWeight={300}
                desktopLineHeight={"1.5"}
                mobileLineHeight={"1.45"}
                color={textBlack}
                sx={{ letterSpacing: "-0.36px" }}
              >
                {fields}
              </NewText>
            </InfoItem>
          )}
        </InfoBlock>
      </Content>
      <Actions>
        <Link href={jobHref} onClick={handleJobClick} tabIndex={-1} role="presentation" style={{ textDecoration: "none" }}>
          <CtaButton
            variant="outlined"
            tabIndex={-1}
            role="presentation"
            sx={{
              whiteSpace: "nowrap",
              borderWidth: "1px",
              background: "none !important",
              "&:hover": {
                borderWidth: "2px",
                borderColor: sharedColors.darkBlue4rentMarketing,
                color: sharedColors.darkBlue4rentMarketing,
              },
            }}
          >
            {messages.more}
          </CtaButton>
        </Link>
      </Actions>
    </StyledCard>
  )

  return (
    <StyledLinkWrapp href={jobHref} onClick={handleJobClick}>
      {cardContent}
    </StyledLinkWrapp>
  )
}

export default JobCard
